import type { ChangeEvent } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

/**
 * The wall-clock time on the device, in the HH:MM shape every log's Time field
 * takes. Logs are written while the thing is happening, so a fresh entry row
 * starts on "now" and the crew only types when correcting or backfilling.
 */
export const currentLocalTime = (): string => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const HOUR_MAX = 23;
const MINUTE_MAX = 59;

const clamp = (digits: string, max: number): string =>
  Number(digits) > max ? String(max) : digits;

/**
 * Lays the digits typed into a Time field out under the HH:MM mask.
 *
 * The colon is punctuation the crew never types: it appears once the hour is
 * complete and disappears with the digit it followed. A leading 3–9 can only be
 * an "0X" hour, so it completes the hour on its own and the next keystroke is
 * already a minute — the same for a 6–9 first minute digit. Anything still out
 * of range is pinned to the nearest real time rather than refused, so the field
 * cannot hold a reading that isn't a time.
 *
 * `autoColon` is what makes backspace work: without it "14:" would grow its
 * colon back the instant the crew deleted it.
 */
export const maskTimeInput = (raw: string, autoColon = true): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (!digits) return '';

  const hourTakesOneDigit = Number(digits[0]) > 2;
  const hourDigits = hourTakesOneDigit ? `0${digits[0]}` : digits.slice(0, 2);
  if (hourDigits.length < 2) return hourDigits;

  const hh = clamp(hourDigits, HOUR_MAX);
  const minuteRest = digits.slice(hourTakesOneDigit ? 1 : 2);
  if (!minuteRest) return autoColon ? `${hh}:` : hh;

  const minuteDigits = Number(minuteRest[0]) > 5 ? `0${minuteRest[0]}` : minuteRest.slice(0, 2);
  return `${hh}:${clamp(minuteDigits, MINUTE_MAX)}`;
};

/**
 * A registered Time field, wearing the mask. Spread in place of `register(...)`:
 * the input stays uncontrolled and react-hook-form still reads the value off the
 * event, it just reads the masked one.
 */
export const timeMaskProps = (field: UseFormRegisterReturn) => ({
  ...field,
  inputMode: 'numeric' as const,
  maxLength: 5,
  onChange: (event: ChangeEvent<HTMLInputElement>) => {
    const deleting =
      'inputType' in event.nativeEvent
        ? String((event.nativeEvent as InputEvent).inputType).startsWith('delete')
        : false;
    event.target.value = maskTimeInput(event.target.value, !deleting);
    return field.onChange(event);
  },
});
